// Guarded top-to-bottom (was unguarded, assuming the navbar always
// existed — true when only sections/hero.liquid rendered it on the
// homepage, no longer true now that snippets/navbar.liquid also renders
// from sections/site-header.liquid on every other page, see that
// section's own comment). Kept as a defensive guard rather than removed
// now that the navbar is sitewide: main.js itself already loads
// sitewide (see layout/theme.liquid's own comment on that), so this
// matches the same "bail out via its own early return" convention every
// other init function in this file already follows.
const navToggle = document.getElementById("navToggle");

if (navToggle) {
  const navbar = navToggle.closest(".navbar");
  const navLinks = document.getElementById("navLinks");

  // The mobile menu's clip-path wipe opens from the toggle button's own
  // on-screen position (see .navbar__links in styles.css) — measured here
  // rather than hardcoded, since a hardcoded guess is exactly what broke
  // this nav before (top:100% resolving against the wrong ancestor). Set
  // on every open, not just once, so it stays correct if the layout shifts
  // (font load, resize) between opens.
  var setMenuOrigin = function () {
    const r = navToggle.getBoundingClientRect();
    navLinks.style.setProperty("--menu-origin-x", `${r.left + r.width / 2}px`);
    navLinks.style.setProperty("--menu-origin-y", `${r.top + r.height / 2}px`);
  };

  const setNavOpen = function (open) {
    if (open) setMenuOrigin();
    navbar.classList.toggle("nav-open", open);
    navToggle.setAttribute("aria-expanded", String(open));
    navToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    // Belt-and-braces alongside the CSS `body:has(.nav-open)` scroll lock —
    // keeps behavior consistent on the rare browser without :has() support.
    document.body.style.overflow = open ? "hidden" : "";
  };

  navToggle.addEventListener("click", () => {
    setNavOpen(navToggle.getAttribute("aria-expanded") !== "true");
  });

  navbar.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && navbar.classList.contains("nav-open")) {
      setNavOpen(false);
      navToggle.focus();
    }
  });

  // Menu links (and the "Join waitlist" pill) are all in-page anchors —
  // close the menu on click so it doesn't stay open, full-screen, over
  // the section being scrolled to. Dropdown triggers (e.g. "Explore")
  // are excluded — clicking one should open its own panel/accordion, not
  // close the whole mobile menu; see initNavDropdowns() below for what
  // handles those.
  navLinks.addEventListener("click", (e) => {
    if (e.target.closest(".navbar__links-trigger")) return;
    if (e.target.closest("a")) setNavOpen(false);
  });

  // Dropdown nav items (e.g. "Explore", set up in Admin under main-menu —
  // see snippets/navbar.liquid's {% if link.links.size > 0 %}). One
  // mechanism drives both presentations: CSS alone decides whether that's
  // a floating panel (desktop) or an inline accordion (inside the mobile
  // full-screen menu, see styles.css's ≤960px block) — this only owns
  // open/close state.
  const items = document.querySelectorAll(".navbar__links-item--has-dropdown");

  const closeAllDropdowns = function (except) {
    items.forEach((item) => {
      if (item === except) return;
      item.classList.remove("is-open");
      item.querySelector(".navbar__links-trigger")?.setAttribute("aria-expanded", "false");
    });
  };

  items.forEach((item) => {
    const trigger = item.querySelector(".navbar__links-trigger");
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      const isOpen = item.classList.toggle("is-open");
      trigger.setAttribute("aria-expanded", String(isOpen));
      closeAllDropdowns(item);
    });
  });

  document.addEventListener("click", (e) => {
    if (!e.target.closest(".navbar__links-item--has-dropdown")) closeAllDropdowns();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeAllDropdowns();
  });
}

// Scroll-scrubbed word reveal ("scroll to reveal" section). The section is
// 220vh tall with a sticky 100vh stage inside it, so scrolling through that
// extra height drives a 0→1 progress value (equivalent to Motion's
// useScroll({ offset: ["start start", "end end"] })). Each word gets its
// own slice of that 0→1 range and fades from a low starting opacity up to
// full as progress passes through its slice.
function initScrollWordReveal() {
  const section = document.getElementById("scrollReveal");
  if (!section) return;

  const heading = section.querySelector(".scroll-reveal__heading");
  const progressFill = section.querySelector(".scroll-reveal__progress span");
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  // Split into words while preserving the manual <br> line breaks already
  // in the markup (they set the exact line layout from the source design —
  // don't lose them by flattening to textContent).
  //
  // ADDED 2026-09-01, per Martina: mobile wants its own line-break points
  // (8 lines) rather than desktop's existing 7. Rebuilding from a
  // different SOURCE string (this element's own innerHTML for desktop, or
  // its data-mobile-html attribute for mobile) rather than trying to keep
  // two sets of <br> tags in the live markup — everything below this line
  // (word-splitting, span-building, the whole scrub timeline) is
  // completely unaffected either way, since both sources produce the same
  // shape of output (a series of <br>-separated lines) for the rest of
  // this function to process identically. Matches sections/statement
  // .liquid's own 960px breakpoint for this exact section (not the 950px
  // line other unrelated sections use) — see that file's own comment.
  // Captured once here, not resize-reactive, same convention every other
  // once-per-load mobile check in this file/theme already follows (a
  // resize crossing 960px would need a full page reload to re-flow the
  // reveal correctly regardless, since the DOM has already been rebuilt
  // into word spans by the time any resize could fire).
  const isMobileHeading = window.matchMedia("(max-width: 960px)").matches;
  const sourceHTML =
    isMobileHeading && heading.dataset.mobileHtml
      ? heading.dataset.mobileHtml
      : heading.innerHTML;

  const lines = sourceHTML
    .split(/<br\s*\/?>/i)
    .map((line) => line.trim());
  heading.innerHTML = "";
  const wordEls = [];
  lines.forEach((line, lineIndex) => {
    const words = line.split(/\s+/).filter(Boolean);
    words.forEach((word, wordIndex) => {
      const span = document.createElement("span");
      span.className = "word";
      span.setAttribute("aria-hidden", "true");
      span.textContent = word;
      heading.appendChild(span);
      wordEls.push(span);
      if (wordIndex < words.length - 1) {
        heading.appendChild(document.createTextNode(" "));
      }
    });
    if (lineIndex < lines.length - 1) {
      heading.appendChild(document.createElement("br"));
    }
  });

  if (reducedMotion) return;

  const SPREAD = 0.8;
  const WORD_DURATION = 0.2;
  const START_OPACITY = 0.15;
  const count = wordEls.length;

  const ranges = wordEls.map((_, index) => {
    const start = count <= 1 ? 0 : (index / (count - 1)) * SPREAD;
    return { start, end: Math.min(1, start + WORD_DURATION) };
  });

  function getWordOpacity(progress, { start, end }) {
    if (progress <= start) return START_OPACITY;
    if (progress >= end) return 1;
    const t = (progress - start) / (end - start);
    return START_OPACITY + (1 - START_OPACITY) * t;
  }

  let ticking = false;

  function update() {
    ticking = false;
    const rect = section.getBoundingClientRect();
    const vh = window.innerHeight;
    const denom = vh - rect.height;
    const raw = denom !== 0 ? rect.top / denom : 0;
    const progress = Math.min(1, Math.max(0, raw));

    if (progressFill) {
      progressFill.style.transform = `scaleY(${progress})`;
    }
    wordEls.forEach((el, i) => {
      el.style.opacity = String(getWordOpacity(progress, ranges[i]));
    });
  }

  function onScrollOrResize() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  // SHOPIFY 2026-08-29: was window-only. Horizon's own base.css swaps the
  // real scrolling element from window to .page-wrapper at >=990px (html/
  // body get overflow:hidden there) — window then never fires a `scroll`
  // event at all at that width, so update() ran exactly once (on load) and
  // then just sat frozen, which is why this read as "not working" on
  // desktop while working fine on mobile/tablet. getBoundingClientRect()
  // itself is unaffected by which element scrolls (it's always relative to
  // the viewport), so the fix is only about what actually wakes update()
  // up — listening on both window and .page-wrapper covers both cases
  // without needing a width check: only one of the two ever actually
  // fires scroll events at a given width, so there's no double-driving.
  const pageWrapper = document.querySelector(".page-wrapper");

  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  pageWrapper?.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);
  update();
}

initScrollWordReveal();

// Floating "Get early access" — hidden while the hero's own inline CTA
// (#heroCta) is on screen, docked bottom-center for the rest of the page
// once it scrolls out. Observing the button itself rather than the whole
// hero section also hands IntersectionObserver's own
// entry.boundingClientRect as the exact source position for the FLIP
// below, with no extra measurement/timing to get right.
function initFloatingCta() {
  const heroCta = document.getElementById("heroCta");
  const floatingCta = document.getElementById("floatingCta");
  if (!heroCta || !floatingCta) return;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  function showFrom(sourceRect) {
    floatingCta.classList.add("is-visible");
    if (reducedMotion) return;

    // FLIP: measure where the button sits once docked (fly offset at
    // rest is 0,0), work out the delta from the hero button's last
    // on-screen rect, jump there instantly (transition off), then
    // re-enable the transition and reset the offset to 0 — the button
    // visually flies from the hero's position down to its fixed dock
    // instead of fading in in place.
    floatingCta.style.transition = "none";
    floatingCta.style.setProperty("--fly-x", "0px");
    floatingCta.style.setProperty("--fly-y", "0px");
    const dockedRect = floatingCta.getBoundingClientRect();

    const dx =
      sourceRect.left +
      sourceRect.width / 2 -
      (dockedRect.left + dockedRect.width / 2);
    const dy = sourceRect.top - dockedRect.top;

    floatingCta.style.setProperty("--fly-x", `${dx}px`);
    floatingCta.style.setProperty("--fly-y", `${dy}px`);
    void floatingCta.offsetWidth; // force reflow before re-enabling the transition
    floatingCta.style.transition = "";
    floatingCta.style.setProperty("--fly-x", "0px");
    floatingCta.style.setProperty("--fly-y", "0px");
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        floatingCta.classList.remove("is-visible");
      } else {
        showFrom(entry.boundingClientRect);
      }
    },
    { threshold: 0 }
  );
  observer.observe(heroCta);
}

initFloatingCta();

// FAQ accordion — one item open at a time. Sets max-height from the
// answer's actual scrollHeight rather than relying on the CSS-only
// grid-template-rows 0fr/1fr trick, which didn't resolve to the content's
// real height consistently across engines in testing.
function initFaq() {
  const items = document.querySelectorAll(".faq__item");

  function close(item) {
    item.classList.remove("is-open");
    item.querySelector(".faq__question").setAttribute("aria-expanded", "false");
    item.querySelector(".faq__answer").style.maxHeight = "";
  }

  function open(item) {
    item.classList.add("is-open");
    item.querySelector(".faq__question").setAttribute("aria-expanded", "true");
    const answer = item.querySelector(".faq__answer");
    answer.style.maxHeight = `${answer.scrollHeight}px`;
  }

  items.forEach((item) => {
    const question = item.querySelector(".faq__question");
    question.addEventListener("click", () => {
      const isOpen = item.classList.contains("is-open");
      items.forEach(close);
      if (!isOpen) open(item);
    });
  });

  // Answers can change height on resize (text reflow at a narrower
  // width) while open — keep max-height in sync so it doesn't clip.
  window.addEventListener("resize", () => {
    items.forEach((item) => {
      if (item.classList.contains("is-open")) {
        const answer = item.querySelector(".faq__answer");
        answer.style.maxHeight = `${answer.scrollHeight}px`;
      }
    });
  });
}

initFaq();

// Spectrogram player — purely decorative for now (see the HTML comment
// above the markup): just swaps the play/pause icon on click so the
// control doesn't look dead. No audio wired up yet.
// Real playback now (assets/breath-recording.wav) — was purely decorative
// before. Icon state driven off the <audio> element's own play/pause/ended
// events rather than toggled on click, so it stays correct even if
// playback stops for a reason other than the button (reaching the end,
// browser autoplay policy, etc.).
function initSpectrogramPlayer() {
  const playBtn = document.getElementById("spectrogramPlay");
  const restartBtn = document.getElementById("spectrogramRestart");
  const audio = document.getElementById("spectrogramAudio");
  if (!playBtn || !audio) return;

  const playIcon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
  const pauseIcon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';

  playBtn.addEventListener("click", () => {
    if (audio.paused) audio.play();
    else audio.pause();
  });

  restartBtn?.addEventListener("click", () => {
    audio.currentTime = 0;
    audio.play();
  });

  audio.addEventListener("play", () => {
    playBtn.innerHTML = pauseIcon;
    playBtn.setAttribute("aria-label", "Pause");
  });
  audio.addEventListener("pause", () => {
    playBtn.innerHTML = playIcon;
    playBtn.setAttribute("aria-label", "Play");
  });
  audio.addEventListener("ended", () => {
    audio.currentTime = 0;
  });
}

initSpectrogramPlayer();

// ADDED 2026-09-01, per Martina ("'join the waitlist' cta button [on the
// coming-soon pages] takes you back to main page hero rather than
// straight to filling the survey"): links to "/#early-access" (the
// navbar CTA on every page, and the coming-soon pages' primary button)
// land here as a full page load with location.hash already set to
// "#early-access". Same root cause as initScrollWordReveal's own comment
// above: Horizon's base.css hands real scrolling off from window/body to
// .page-wrapper at >=990px (html/body get overflow:hidden there). Native
// hash-scroll targets window/body — at that width there's nothing for it
// to move, so the page just sits at the top (the hero) instead of
// landing on the form. scrollIntoView(), unlike that native behavior,
// walks every scrollable ancestor (not just window), so it correctly
// scrolls .page-wrapper too — one code path fixes both the >=990px and
// <990px cases, no width check needed.
//
// FIXED 2026-09-01 (later same day), per Martina ("why does it scroll so
// fast through the page? can['t] see statement reveal / render animation
// properly"): the first version also had a delayed second corrective
// call (setTimeout 500ms) as insurance against a GSAP pin-spacer still
// settling — but if the visitor had already started scrolling back up on
// their own by then (e.g. to rewatch the statement/device-render
// sections), that second call yanked them straight back down to
// #early-access mid-scroll, reading as the whole page suddenly racing
// through everything. Two changes: dropped the delayed second call
// entirely (window 'load' + a couple animation frames is already late
// enough in practice — it was only ever insurance, not the fix's actual
// load-bearing part), and the single remaining call now bails if the
// visitor has already scrolled/touched the page themselves before it
// fires, so a real person exploring the page is never overridden.
//
// FIXED AGAIN 2026-09-01 (third pass, per Martina: "it broke down after
// we touched the [dead-link] bug... can it be related?" — yes): the
// user-interaction guard above only ever addressed a SECOND corrective
// call firing late — it didn't touch the fact that the ONE remaining
// call still used behavior:"smooth". #early-access sits at the very
// bottom of the homepage, past the scroll-scrubbed statement-reveal and
// device-render sections (each mapping its entire GSAP timeline onto a
// tall CSS min-height, see assets/styles.css). A *smooth* scroll from
// the top all the way down there has to animate through that whole
// distance, which means visibly flying through both sections' pinned
// animations at high speed on the way past — this is likely what read
// as "the page scrolls itself super fast," not general scroll physics,
// and explains why widening those sections' scroll budget earlier today
// didn't help (a wider budget still gets blown through just as fast by
// an automatic multi-thousand-pixel smooth-scroll). Switched to
// behavior:"auto" (an instant jump, not animated) — this also matches
// what a plain browser hash-jump does by default (smooth-scrolling
// anchors was never the goal here, just *landing correctly*, which the
// section's own root-cause comment above already covers).
//
// SIMPLIFIED 2026-09-01 (fourth pass), found during a scroll-bug audit
// (not reported by Martina, caught by reading the code): the
// wheel/touchmove/page-wrapper-scroll listeners above armed the
// "did the visitor already move?" guard from the moment this script ran
// — i.e. from partway through the initial page parse, well before
// `load` fires. On a slow initial load (the 3D model, custom fonts, and
// several images all still fetching), a visitor who scrolled/touched the
// page even slightly *during* that load — before the auto-scroll this
// function schedules ever got a chance to run — would permanently trip
// `userInteracted`, silently skipping the auto-scroll and reverting to
// the exact original bug this whole function exists to fix (landing on
// the hero, not the form), with no error and no way to tell why. Dropped
// the event-listener guard entirely and replaced it with a direct check
// of the actual scroll position right before firing: if nothing has
// moved the page from the very top by the time the auto-scroll is about
// to run, land it; if something has (the visitor scrolling on their own,
// during load or after), leave them where they are. Same intent as the
// listener version — never override a visitor who's already looking at
// something — but checked at the one moment it actually matters instead
// of guessed at from a window that started too early.
function initHashScroll() {
  if (!location.hash) return;

  const target = document.querySelector(location.hash);
  if (!target) return;

  window.addEventListener("load", () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const pageWrapper = document.querySelector(".page-wrapper");
        const currentScroll = pageWrapper ? pageWrapper.scrollTop : window.scrollY;
        if (currentScroll === 0) {
          target.scrollIntoView({ behavior: "auto", block: "start" });
        }
      });
    });
  });
}

initHashScroll();
